import { zodParse } from 'base/zod_parse';
import finsweetVariables from './finsweet_variables.json';
import relumeVariables from './relume_variables.json';
import { Component, componentSchema, WfVariable } from './schema';

export function inlineRelumeAndFinsweetVariables(
  component: Component,
): Component {
  let componentText = JSON.stringify(component);
  let fsVariables = finsweetVariables as WfVariable[];
  let rlVariables = relumeVariables as WfVariable[];
  let variables = [...fsVariables, ...rlVariables];
  let variableIdToVariableMap = new Map(variables.map((v) => [v.id, v]));
  for (let variable of variables) {
    componentText = componentText.replaceAll(
      `@var_${variable.id}`,
      variableToCSSValue(variable, variableIdToVariableMap),
    );
  }
  return JSON.parse(componentText);
}

export function convertRelumeToFinsweetVariables(
  component: Component,
): Component {
  // some finsweet variables change "-Color " to ""- Base Color -"
  let nameToFinsweetVariableMap = new Map(
    finsweetVariables.map((v) => [v.name, v]),
  );
  for (let variable of nameToFinsweetVariableMap.values()) {
    if (variable.name.includes('- Base Color -')) {
      let newName = variable.name.replace('- Base Color -', '-Color');
      nameToFinsweetVariableMap.set(newName, variable);
    }
  }

  let relumeVariableIdToFinsweetVariableIdMap = new Map(
    relumeVariables.map((v) => [
      v.id,
      nameToFinsweetVariableMap.get(v.name)?.id,
    ]),
  );

  let componentText = JSON.stringify(component);
  for (let variable of relumeVariables) {
    let mappedId = relumeVariableIdToFinsweetVariableIdMap.get(variable.id);
    if (!mappedId) {
      continue;
    }
    componentText = componentText.replaceAll(variable.id, mappedId);
  }
  return zodParse(componentSchema, JSON.parse(componentText));
}

export function inlineVariableValues(
  component: Component,
  variables: WfVariable[],
): Component {
  let componentText = JSON.stringify(component);
  let variableIdToVariableMap = new Map(variables.map((v) => [v.id, v]));
  for (let variable of variables) {
    componentText = componentText.replaceAll(
      `@var_${variable.id}`,
      variableToCSSValue(variable, variableIdToVariableMap),
    );
  }
  return JSON.parse(componentText);
}

export function variableToCSSValue(
  variable: WfVariable | undefined,
  variableIdToVariableMap: Map<string, WfVariable>,
): string {
  let variableValue = variable?.value;
  if (!variable || !variableValue) {
    throw new Error('variable does not have a value');
  }
  if (variableValue.type === 'ref') {
    // must be a reference variable
    return variableToCSSValue(
      variableIdToVariableMap.get(variableValue.value.variableId),
      variableIdToVariableMap,
    );
  } else if (variableValue.type === 'font-family') {
    return variableValue.value;
  } else if (variableValue.type === 'color') {
    return variableValue.value;
  } else if (variableValue.type === 'length') {
    return `${variableValue.value.value}${variableValue.value.unit}`;
  } else {
    throw new Error(`unknown variable type ${variable}`);
  }
}
